/* NCERT Wala — Bookstore indents.
   Completely separate from the retail store: this page never touches the cart, and retail stock never
   limits or changes an indent. The catalogue comes from Shopify's public product feed, the indent is kept
   in this browser, and on submit Shopify's contact form emails the full indent to NCERT Wala, who then
   sends a Draft Order invoice (payment link). */
(function () {
  'use strict';

  var root = document.querySelector('[data-bulk]');
  if (!root) return;

  var config = JSON.parse(document.querySelector('[data-bulk-config]').textContent);
  var MAX_QTY = Number(config.maxQty) || 100000;
  var KEY_INDENT = 'ncw-bulk-indent-v1';
  var KEY_DETAILS = 'ncw-bulk-details-v1';
  var KEY_PENDING = 'ncw-bulk-pending-v1';
  var STREAMS = ['Science', 'Commerce', 'Humanities'];

  /* ---------- storage (never throws) ---------- */
  function store(kind) { try { return window[kind]; } catch (e) { return null; } }
  function load(key, kind) {
    var s = store(kind || 'localStorage');
    try { return s ? JSON.parse(s.getItem(key)) : null; } catch (e) { return null; }
  }
  function save(key, value, kind) {
    var s = store(kind || 'localStorage');
    try { if (s) s.setItem(key, JSON.stringify(value)); } catch (e) { /* storage full or blocked */ }
  }
  function drop(key, kind) {
    var s = store(kind || 'localStorage');
    try { if (s) s.removeItem(key); } catch (e) { /* ignore */ }
  }

  /* ---------- helpers ---------- */
  var $ = function (sel, el) { return (el || root).querySelector(sel); };
  var $$ = function (sel, el) { return Array.prototype.slice.call((el || root).querySelectorAll(sel)); };
  // Customer-facing amounts: whole rupees (display only — totals are calculated from exact paise).
  var moneyFmt = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  function money(paise) { return config.currencySymbol + moneyFmt.format(paise / 100); }
  // Staff email only: exact to the paisa, so the Draft Order is built from the real figures.
  var preciseFmt = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  function moneyPrecise(paise) { return config.currencySymbol + preciseFmt.format(paise / 100); }
  function num(n) { return Number(n).toLocaleString('en-IN'); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function plural(n, word) { return num(n) + ' ' + word + (n === 1 ? '' : 's'); }

  /* ---------- catalogue ---------- */
  var books = [];   // [{ id, title, url, image, price, cls, subject, streams, search }]
  var byId = {};
  var indent = load(KEY_INDENT) || {};   // { variantId: copies }

  function classOf(product) {
    for (var i = 0; i < product.tags.length; i++) {
      var m = /^class\s*:?\s*(\d{1,2})$/i.exec(product.tags[i].trim());
      if (m) return Number(m[1]);
    }
    var t = /\bclass\s*(\d{1,2})\b/i.exec(product.title);
    return t ? Number(t[1]) : null;
  }
  function tagValue(product, prefix) {
    for (var i = 0; i < product.tags.length; i++) {
      var tag = product.tags[i];
      if (tag.toLowerCase().indexOf(prefix + ':') === 0) return tag.slice(prefix.length + 1).trim();
    }
    return '';
  }
  function subjectOf(product) {
    var tagged = tagValue(product, 'subject');
    if (tagged) return tagged;
    var m = /[–-]\s*([^,–]+?),\s*class\s*\d/i.exec(product.title) || /^([^,–]+?),\s*class\s*\d/i.exec(product.title);
    return m ? m[1].trim() : '';
  }
  function streamsOf(product, cls, subject) {
    if (cls !== 11 && cls !== 12) return [];
    var tagged = tagValue(product, 'stream');
    if (tagged) return [tagged.charAt(0).toUpperCase() + tagged.slice(1).toLowerCase()];
    var text = (product.title + ' ' + subject).toLowerCase();
    if (/physics|chemistry|biology/.test(text)) return ['Science'];
    if (/accountancy|business studies/.test(text)) return ['Commerce'];
    if (/mathematic/.test(text)) return ['Science', 'Commerce'];
    if (/economic/.test(text)) return ['Commerce', 'Humanities'];
    if (/history|geograph|political|sociology|psycholog|society/.test(text)) return ['Humanities'];
    return STREAMS.slice(); // languages and books common to all streams
  }
  function streamLabel(streams) {
    if (!streams.length) return '';
    return streams.length === STREAMS.length ? 'All streams' : streams.join(' · ');
  }
  function metaLine(book) {
    var parts = [];
    if (book.subject) parts.push(book.subject);
    if (book.cls) parts.push('Class ' + book.cls);
    if (book.streams.length) parts.push(streamLabel(book.streams));
    return parts.join(' · ');
  }

  function loadCatalogue() {
    var all = [];
    function page(n) {
      return fetch(config.rootUrl + 'products.json?limit=250&page=' + n, { headers: { Accept: 'application/json' } })
        .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
        .then(function (data) {
          var list = data.products || [];
          all = all.concat(list);
          return list.length === 250 && n < 40 ? page(n + 1) : all;
        });
    }
    return page(1).then(function (products) {
      products.forEach(function (p) {
        // Indents are for NCERT textbooks only; private publisher books and registers stay retail.
        var type = (p.product_type || '').toLowerCase();
        if (type === 'private publisher' || type === 'registers & notebooks') return;
        var cls = classOf(p);
        var subject = subjectOf(p);
        var streams = streamsOf(p, cls, subject);
        var image = p.images && p.images[0] ? p.images[0].src : '';
        p.variants.forEach(function (v) {
          var book = {
            id: String(v.id),
            productId: String(p.id),
            sku: v.sku || '',
            title: p.title + (p.variants.length > 1 ? ' — ' + v.title : ''),
            url: config.rootUrl + 'products/' + p.handle,
            image: image ? image + (image.indexOf('?') > -1 ? '&' : '?') + 'width=96' : '',
            price: Math.round(parseFloat(v.price) * 100),
            cls: cls,
            subject: subject,
            streams: streams
          };
          book.search = (book.title + ' ' + subject + ' class ' + cls + ' ' + streams.join(' ')).toLowerCase();
          books.push(book);
          byId[book.id] = book;
        });
      });
      books.sort(function (a, b) { return (a.cls || 99) - (b.cls || 99) || a.title.localeCompare(b.title); });
    });
  }

  function renderCatalogue() {
    var groups = {};
    books.forEach(function (b) { (groups[b.cls || 'other'] = groups[b.cls || 'other'] || []).push(b); });
    var keys = Object.keys(groups).sort(function (a, b) { return (Number(a) || 99) - (Number(b) || 99); });
    $('[data-catalogue]').innerHTML = keys.map(function (key) {
      var list = groups[key];
      return '<section class="bulk-class" data-group="' + esc(key) + '">' +
        '<h3 class="bulk-class__title">' + (key === 'other' ? 'Other books' : 'Class ' + key) + ' <span>' + plural(list.length, 'title') + '</span></h3>' +
        '<ul class="bulk-list">' + list.map(rowHTML).join('') + '</ul></section>';
    }).join('');
    $$('.bulk-row').forEach(syncRow);
  }

  function rowHTML(b) {
    var qid = 'q-' + b.id;
    return '<li class="bulk-row" data-id="' + b.id + '">' +
      '<span class="bulk-row__img">' + (b.image ? '<img src="' + esc(b.image) + '" alt="" width="48" height="64" loading="lazy">' : '') + '</span>' +
      '<span class="bulk-row__info">' +
        '<a class="bulk-row__title" href="' + esc(b.url) + '" target="_blank" rel="noopener">' + esc(b.title) + '<span class="sr-only"> (opens in a new tab)</span></a>' +
        '<span class="bulk-row__meta">' + esc(metaLine(b)) + '</span>' +
      '</span>' +
      '<span class="bulk-row__price">' + money(b.price) + '</span>' +
      '<span class="bulk-row__qty">' +
        '<label class="sr-only" for="' + qid + '">Copies of ' + esc(b.title) + '</label>' +
        '<input class="input bulk-qty" id="' + qid + '" type="number" min="0" max="' + MAX_QTY + '" step="1" inputmode="numeric" placeholder="0">' +
        '<button class="btn btn--sm" type="button" data-commit>Add</button>' +
      '</span>' +
      '<span class="bulk-row__error" role="alert"></span>' +
    '</li>';
  }

  /* Validates a typed quantity. Empty counts as 0 (not in the indent). */
  function parseQty(raw, input) {
    if (input && input.validity && input.validity.badInput) return { ok: false, message: 'Enter a whole number of copies.' };
    var value = String(raw).trim();
    if (value === '') return { ok: true, qty: 0 };
    if (!/^\d+$/.test(value)) return { ok: false, message: 'Enter a whole number of copies.' };
    var qty = Number(value);
    if (qty > MAX_QTY) return { ok: false, message: 'Maximum ' + num(MAX_QTY) + ' copies per title in one indent.' };
    return { ok: true, qty: qty };
  }

  function syncRow(row) {
    var id = row.getAttribute('data-id');
    var input = $('.bulk-qty', row);
    var button = $('[data-commit]', row);
    var error = $('.bulk-row__error', row);
    var inIndent = indent[id] || 0;
    if (document.activeElement !== input) input.value = inIndent ? inIndent : '';
    var typed = parseQty(input.value, input);
    row.classList.toggle('is-selected', inIndent > 0);
    if (!typed.ok) {
      input.setAttribute('aria-invalid', 'true');
      error.textContent = typed.message;
      button.textContent = inIndent ? 'Update' : 'Add';
      button.disabled = true;
      return;
    }
    input.removeAttribute('aria-invalid');
    error.textContent = '';
    button.disabled = false;
    if (inIndent && typed.qty === inIndent) { button.textContent = '✓ Added'; button.disabled = true; }
    else if (inIndent && typed.qty === 0) button.textContent = 'Remove';
    else if (inIndent) button.textContent = 'Update';
    else button.textContent = 'Add';
  }

  function commitRow(row) {
    var input = $('.bulk-qty', row);
    var parsed = parseQty(input.value, input);
    if (!parsed.ok) { syncRow(row); return; }
    setQty(row.getAttribute('data-id'), parsed.qty);
  }

  function setQty(id, qty) {
    if (qty > 0) indent[id] = qty; else delete indent[id];
    save(KEY_INDENT, indent);
    var row = $('.bulk-row[data-id="' + id + '"]');
    if (row) syncRow(row);
    updateIndentBar();
  }

  /* ---------- filters ---------- */
  var filterTimer;
  function applyFilters() {
    var q = $('[data-filter-search]').value.trim().toLowerCase();
    var terms = q ? q.split(/\s+/) : [];
    var cls = $('[data-filter-class]').value;
    var streamRelevant = cls === '11' || cls === '12';
    var stream = streamRelevant ? $('[data-filter-stream]').value : '';
    var selectedOnly = $('[data-filter-selected]').checked;
    $('[data-stream-field]').hidden = !streamRelevant;

    var shown = 0;
    $$('.bulk-class').forEach(function (group) {
      var visibleInGroup = 0;
      $$('.bulk-row', group).forEach(function (row) {
        var b = byId[row.getAttribute('data-id')];
        var visible = (!cls || String(b.cls) === cls) &&
          (!stream || b.streams.indexOf(stream) > -1) &&
          (!selectedOnly || indent[b.id]) &&
          terms.every(function (t) { return b.search.indexOf(t) > -1; });
        row.hidden = !visible;
        if (visible) visibleInGroup++;
      });
      group.hidden = visibleInGroup === 0;
      shown += visibleInGroup;
    });
    $('[data-catalogue-status]').textContent = shown === books.length
      ? 'Showing all ' + plural(books.length, 'book') + '.'
      : 'Showing ' + num(shown) + ' of ' + plural(books.length, 'book') + '.' + (shown === 0 ? ' Try a different search or class.' : '');
  }

  /* ---------- indent ---------- */
  function indentLines() {
    return Object.keys(indent).filter(function (id) { return byId[id]; }).map(function (id) {
      return { book: byId[id], qty: indent[id] };
    }).sort(function (a, b) {
      return (a.book.cls || 99) - (b.book.cls || 99) || a.book.title.localeCompare(b.book.title);
    });
  }
  /* All indent maths in one place. Amounts are in paise.
     1. Subtotal of the NCERT books at catalogue prices.
     2. Bookstore bulk discount, applied once to that subtotal (indents only, never retail).
     3. GST on the discounted book value at the configured rate for NCERT books (0%).
     Delivery charges are separate and confirmed by NCERT Wala; they are never folded into the GST line. */
  var DISCOUNT_PERCENT = Math.max(0, Number(config.discountPercent) || 0);
  var GST_PERCENT = Math.max(0, Number(config.gstPercent) || 0);
  function summarize(items, percent, gstPercent) {
    var p = percent == null ? DISCOUNT_PERCENT : percent;
    var g = gstPercent == null ? GST_PERCENT : gstPercent;
    var subtotal = items.reduce(function (s, i) { return s + (i.book ? i.book.price : i.price) * i.qty; }, 0);
    var discount = Math.round(subtotal * p / 100);
    var afterDiscount = subtotal - discount;
    var gst = Math.round(afterDiscount * g / 100);
    return {
      titles: items.length,
      books: items.reduce(function (s, i) { return s + i.qty; }, 0),
      subtotal: subtotal,
      percent: p,
      discount: discount,
      afterDiscount: afterDiscount,
      gstPercent: g,
      gst: gst,
      total: afterDiscount + gst
    };
  }
  var exactFmt = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  function moneyExact(paise) { return config.currencySymbol + exactFmt.format(paise / 100); }
  function gstLabel(t) { return 'GST (' + t.gstPercent + '%' + (t.gstPercent === 0 ? ' on NCERT books' : '') + ')'; }
  function estimateLabel() { return 'Final estimated total'; }
  var DELIVERY_TEXT = 'If applicable — confirmed separately';
  function estimateNote() {
    return 'Estimate only, plus delivery charges if applicable. Stock availability, final pricing and delivery charges are confirmed by NCERT Wala before payment. This indent is not a tax invoice.';
  }

  function updateIndentBar() {
    var t = summarize(indentLines());
    $('[data-indent-titles]').textContent = num(t.titles);
    $('[data-indent-books]').textContent = num(t.books);
    $('[data-indent-subtotal]').textContent = money(t.total);
  }

  function totalsHTML(t) {
    return '<dl class="bulk-totals">' +
      '<div><dt>Titles</dt><dd>' + num(t.titles) + '</dd></div>' +
      '<div><dt>Total books</dt><dd>' + num(t.books) + '</dd></div>' +
      '<div><dt>Subtotal before discount</dt><dd>' + money(t.subtotal) + '</dd></div>' +
      (t.percent ? '<div class="is-discount"><dt>Bookstore bulk discount (' + t.percent + '%)</dt><dd>−' + money(t.discount) + '</dd></div>' : '') +
      '<div><dt>Subtotal after discount</dt><dd>' + money(t.afterDiscount) + '</dd></div>' +
      '<div><dt>' + gstLabel(t) + '</dt><dd>' + moneyExact(t.gst) + '</dd></div>' +
      '<div class="is-pending"><dt>Delivery charges</dt><dd>' + DELIVERY_TEXT + '</dd></div>' +
      '<div class="bulk-totals__main"><dt>' + estimateLabel() + '*</dt><dd>' + money(t.total) + '</dd></div>' +
    '</dl>' +
    '<p class="bulk-totals__foot">* ' + estimateNote() + '</p>';
  }

  function renderIndent() {
    var lines = indentLines();
    var box = $('[data-indent-table]');
    if (!lines.length) {
      box.innerHTML = '<div class="empty-state"><h3>Your indent is empty</h3><p>Add books from the catalogue and enter the number of copies you need.</p>' +
        '<a class="btn" href="#catalogue" data-goto="catalogue">Browse the catalogue</a></div>';
      $('[data-proceed-details]').disabled = true;
      return;
    }
    box.innerHTML =
      '<div class="table-scroll"><table class="cart-table bulk-indent">' +
      '<caption class="sr-only">Books in your indent</caption>' +
      '<thead><tr><th scope="col">Book</th><th scope="col">Copies</th><th scope="col">Catalogue price</th><th scope="col" class="cart-table__total">Line value</th><th scope="col"><span class="sr-only">Remove</span></th></tr></thead><tbody>' +
      lines.map(function (l) {
        var b = l.book;
        return '<tr data-id="' + b.id + '">' +
          '<td data-label="Book"><a href="' + esc(b.url) + '" target="_blank" rel="noopener">' + esc(b.title) + '</a><span class="book-meta">' + esc(metaLine(b)) + '</span></td>' +
          '<td data-label="Copies"><label class="sr-only" for="iq-' + b.id + '">Copies of ' + esc(b.title) + '</label>' +
            '<input class="input bulk-qty" id="iq-' + b.id + '" type="number" min="1" max="' + MAX_QTY + '" step="1" inputmode="numeric" value="' + l.qty + '" data-indent-qty><span class="bulk-row__error" role="alert"></span></td>' +
          '<td data-label="Catalogue price">' + money(b.price) + '</td>' +
          '<td data-label="Line value" class="cart-table__total">' + money(b.price * l.qty) + '</td>' +
          '<td><button class="link-btn" type="button" data-remove>Remove</button></td>' +
        '</tr>';
      }).join('') +
      '</tbody></table></div>' + totalsHTML(summarize(lines));
    $('[data-proceed-details]').disabled = false;
  }

  /* ---------- details form ---------- */
  var form = $('[data-details-form]');
  function normaliseMobile(v) {
    var digits = v.replace(/[\s\-()]/g, '');
    if (/^\+?91\d{10}$/.test(digits)) digits = digits.slice(-10);
    if (/^0\d{10}$/.test(digits)) digits = digits.slice(1);
    return digits;
  }
  var RULES = {
    business: { label: 'Bookstore / Business name', test: function (v) { return v.length >= 2; } },
    contact: { label: "Contact person's name", test: function (v) { return v.length >= 2; } },
    mobile: { label: 'Mobile number', normalise: normaliseMobile, test: function (v) { return /^[6-9]\d{9}$/.test(v); }, message: 'Enter a valid 10-digit Indian mobile number.' },
    email: { label: 'Email address', test: function (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v); }, message: 'Enter a valid email address.' },
    gstin: { label: 'GSTIN', optional: true, normalise: function (v) { return v.toUpperCase().replace(/\s/g, ''); }, test: function (v) { return /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(v); }, message: 'Enter a valid 15-character GSTIN, or leave it empty.' },
    shipAddress: { label: 'Delivery address', test: function (v) { return v.length >= 5; } },
    shipCity: { label: 'Delivery city', test: function (v) { return v.length >= 2; } },
    shipState: { label: 'Delivery state', test: function (v) { return !!v; } },
    shipPin: { label: 'Delivery PIN code', test: function (v) { return /^[1-9]\d{5}$/.test(v); }, message: 'Enter a valid 6-digit PIN code.' },
    billAddress: { label: 'Billing address', billing: true, test: function (v) { return v.length >= 5; } },
    billCity: { label: 'Billing city', billing: true, test: function (v) { return v.length >= 2; } },
    billState: { label: 'Billing state', billing: true, test: function (v) { return !!v; } },
    billPin: { label: 'Billing PIN code', billing: true, test: function (v) { return /^[1-9]\d{5}$/.test(v); }, message: 'Enter a valid 6-digit PIN code.' }
  };

  /* ---------- "Same as delivery address" ----------
     While ticked, the billing address IS the delivery address: the billing fields mirror the delivery
     fields (read-only) and stay in sync as the delivery address is edited. Unticked, they are free to edit
     and start from the last copied values. Ticking with no delivery address entered copies nothing. */
  var ADDRESS_PARTS = ['Address', 'City', 'State', 'Pin'];
  function deliveryHasData() {
    return ADDRESS_PARTS.some(function (p) { return form.elements['ship' + p].value.trim() !== ''; });
  }
  function setBillingLocked(locked) {
    ADDRESS_PARTS.forEach(function (p) {
      var el = form.elements['bill' + p];
      if (el.tagName === 'SELECT') el.disabled = locked; else el.readOnly = locked;
      el.required = !locked;
      el.classList.toggle('is-mirrored', locked);
      if (locked) {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
        var msg = el.closest('.field').querySelector('.field__error');
        if (msg) msg.remove();
      }
    });
  }
  function copyDeliveryToBilling() {
    ADDRESS_PARTS.forEach(function (p) { form.elements['bill' + p].value = form.elements['ship' + p].value; });
  }
  function syncBilling(userAction) {
    var box = form.elements.sameAsDelivery;
    var status = $('[data-same-status]');
    if (box.checked && userAction && !deliveryHasData()) {
      // Nothing to copy: leave the billing fields as they are and let the user type them.
      box.checked = false;
      setBillingLocked(false);
      status.textContent = 'Enter the delivery address first, or fill in the billing address below.';
      return;
    }
    if (box.checked) {
      copyDeliveryToBilling();
      setBillingLocked(true);
      status.textContent = deliveryHasData()
        ? 'Billing address copied from the delivery address and kept in sync.'
        : 'The billing address will copy the delivery address as you enter it.';
    } else {
      setBillingLocked(false);
      status.textContent = userAction ? 'Enter the billing address below.' : '';
    }
  }

  function readDetails() {
    var d = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (el.name) d[el.name] = el.type === 'checkbox' ? el.checked : el.value.trim();
    });
    if (d.sameAsDelivery) {
      ADDRESS_PARTS.forEach(function (p) { d['bill' + p] = d['ship' + p]; });
    }
    return d;
  }
  function fillDetails(d) {
    if (!d) return;
    // Details saved by the previous version of this form ("Same as billing address").
    if (d.sameAsBilling !== undefined && d.sameAsDelivery === undefined) {
      if (d.sameAsBilling) ADDRESS_PARTS.forEach(function (p) { d['ship' + p] = d['bill' + p]; });
      d.sameAsDelivery = !!d.sameAsBilling;
      delete d.sameAsBilling;
    }
    Object.keys(d).forEach(function (name) {
      var el = form.elements[name];
      if (!el) return;
      if (el.type === 'checkbox') el.checked = !!d[name]; else el.value = d[name];
    });
  }

  /* Returns { ok, details, errors } and, when mark is true, flags fields in the form. */
  function validateDetails(mark) {
    var d = readDetails();
    var errors = [];
    Object.keys(RULES).forEach(function (name) {
      var rule = RULES[name];
      var el = form.elements[name];
      var value = d[name] || '';
      if (rule.normalise) value = d[name] = rule.normalise(value);
      // With "Same as delivery address" the billing address is the (validated) delivery address.
      var skip = (rule.billing && d.sameAsDelivery) || (rule.optional && value === '');
      var bad = !skip && !rule.test(value);
      var message = value === '' ? rule.label + ' is required.' : (rule.message || 'Check the ' + rule.label.toLowerCase() + '.');
      if (bad) errors.push({ name: name, message: message });
      if (!mark) return;
      var field = el.closest('.field');
      var msg = field.querySelector('.field__error');
      if (bad) {
        el.setAttribute('aria-invalid', 'true');
        if (!msg) { msg = document.createElement('p'); msg.className = 'field__error'; msg.id = el.id + '-error'; field.appendChild(msg); }
        msg.textContent = message;
        el.setAttribute('aria-describedby', msg.id);
      } else {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
        if (msg) msg.remove();
      }
    });
    return { ok: errors.length === 0, details: d, errors: errors };
  }

  function showFormErrors(errors) {
    var box = $('[data-form-errors]');
    if (!errors.length) { box.hidden = true; box.innerHTML = ''; return; }
    box.innerHTML = '<p><strong>Please correct ' + plural(errors.length, 'field') + ':</strong></p><ul>' +
      errors.map(function (e) { return '<li><a href="#' + form.elements[e.name].id + '">' + esc(e.message) + '</a></li>'; }).join('') + '</ul>';
    box.hidden = false;
    form.elements[errors[0].name].focus();
  }

  function addressText(d, prefix) {
    return [d[prefix + 'Address'], d[prefix + 'City'], d[prefix + 'State'] + ' ' + d[prefix + 'Pin'], 'India'].join(', ');
  }

  /* ---------- review & submit ---------- */
  function newReference() {
    var now = new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    var rand = window.crypto && window.crypto.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(1))[0] % 10000
      : Math.floor(Math.random() * 10000);
    return 'IND-' + now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + '-' + String(rand).padStart(4, '0');
  }

  function detailsHTML(d) {
    return '<div class="bulk-summary">' +
      '<div class="panel"><h3>Bookstore details</h3><dl class="spec-list">' +
        '<div><dt>Business</dt><dd>' + esc(d.business) + '</dd></div>' +
        '<div><dt>Contact</dt><dd>' + esc(d.contact) + '</dd></div>' +
        '<div><dt>Mobile</dt><dd>' + esc(d.mobile) + '</dd></div>' +
        '<div><dt>Email</dt><dd>' + esc(d.email) + '</dd></div>' +
        '<div><dt>GSTIN</dt><dd>' + esc(d.gstin || 'Not provided') + '</dd></div>' +
      '</dl></div>' +
      '<div class="panel"><h3>Addresses</h3><dl class="spec-list">' +
        '<div><dt>Billing</dt><dd>' + esc(addressText(d, 'bill')) + '</dd></div>' +
        '<div><dt>Delivery</dt><dd>' + esc(addressText(d, 'ship')) + '</dd></div>' +
        (d.notes ? '<div><dt>Notes</dt><dd>' + esc(d.notes) + '</dd></div>' : '') +
      '</dl></div>' +
    '</div>';
  }

  function linesTableHTML(lines) {
    return '<div class="table-scroll"><table class="cart-table bulk-indent">' +
      '<caption class="sr-only">Books in this indent</caption>' +
      '<thead><tr><th scope="col">Book</th><th scope="col">Copies</th><th scope="col">Catalogue price</th><th scope="col" class="cart-table__total">Line value</th></tr></thead><tbody>' +
      lines.map(function (l) {
        return '<tr><td data-label="Book">' + esc(l.title) + '<span class="book-meta">' + esc(l.meta) + '</span></td>' +
          '<td data-label="Copies">' + num(l.qty) + '</td>' +
          '<td data-label="Catalogue price">' + money(l.price) + '</td>' +
          '<td data-label="Line value" class="cart-table__total">' + money(l.price * l.qty) + '</td></tr>';
      }).join('') +
      '</tbody></table></div>';
  }

  /* Snapshot of the indent, so the confirmation and email match exactly what was reviewed. */
  function snapshot() {
    return indentLines().map(function (l) {
      return {
        id: l.book.id, productId: l.book.productId, sku: l.book.sku, title: l.book.title, meta: metaLine(l.book),
        cls: l.book.cls, subject: l.book.subject, stream: streamLabel(l.book.streams), price: l.book.price, qty: l.qty
      };
    });
  }

  function renderReview() {
    var check = validateDetails(false);
    var lines = snapshot();
    $('[data-review]').innerHTML = linesTableHTML(lines) + totalsHTML(summarize(lines)) + detailsHTML(check.details);
  }

  function formatDate(ms) {
    return new Date(ms).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'long', timeStyle: 'short' }) + ' IST';
  }

  function emailBody(reference, d, lines, at) {
    var t = summarize(lines);
    var out = [
      'BOOKSTORE INDENT ' + reference,
      'Submitted: ' + formatDate(at),
      'Status: ' + config.status,
      'Separate from retail stock: do not deduct retail inventory. Create a Draft Order and send the invoice to the bookstore.',
      '',
      'BOOKSTORE',
      'Business: ' + d.business,
      'Contact person: ' + d.contact,
      'Mobile: ' + d.mobile,
      'Email: ' + d.email,
      'GSTIN: ' + (d.gstin || 'Not provided'),
      '',
      'Billing address: ' + addressText(d, 'bill'),
      'Delivery address: ' + addressText(d, 'ship'),
      '',
      'BOOKS (' + t.titles + ' titles, ' + t.books + ' copies)'
    ];
    lines.forEach(function (l, i) {
      out.push((i + 1) + '. ' + (l.cls ? 'Class ' + l.cls + ' | ' : '') + l.title + ' | Qty ' + l.qty +
        ' | Catalogue ' + moneyPrecise(l.price) + ' = ' + moneyPrecise(l.price * l.qty) +
        ' | Product ID ' + l.productId + ' / Variant ID ' + l.id + (l.sku ? ' / SKU ' + l.sku : ''));
    });
    out.push('');
    out.push('SUMMARY (catalogue prices)');
    out.push('Total books: ' + t.books);
    out.push('Subtotal before discount: ' + moneyPrecise(t.subtotal));
    if (t.percent) out.push('Bookstore bulk discount (' + t.percent + '%): -' + moneyPrecise(t.discount) + '  (apply ONCE on the Draft Order; do NOT also apply the 5% retail discount)');
    out.push('Subtotal after discount: ' + moneyPrecise(t.afterDiscount));
    out.push(gstLabel(t) + ': ' + moneyPrecise(t.gst));
    out.push('Delivery charges: ' + DELIVERY_TEXT);
    out.push(estimateLabel() + ': ' + moneyPrecise(t.total) + ' + delivery charges, if applicable (estimate)');
    out.push('Stock availability, final pricing and delivery charges: to be confirmed by NCERT Wala before payment. Not a tax invoice.');
    if (d.notes) { out.push(''); out.push('NOTES: ' + d.notes); }
    return out.join('\n');
  }

  var submitForm = document.getElementById('bulk-indent-form');
  submitForm.addEventListener('submit', function (e) {
    var check = validateDetails(false);
    var lines = snapshot();
    if (!lines.length || !check.ok) { e.preventDefault(); go(lines.length ? 'details' : 'catalogue'); return; }
    if (!$('[data-submit-consent]').checked) { e.preventDefault(); $('[data-submit-consent]').focus(); return; }
    var d = check.details;
    var pending = load(KEY_PENDING);
    var reference = pending && !pending.submitted ? pending.reference : newReference();
    var at = Date.now();
    var set = function (name, value) { submitForm.querySelector('[data-submit-field="' + name + '"]').value = value; };
    set('name', d.business + ' — ' + d.contact);
    set('email', d.email);
    set('phone', d.mobile);
    set('reference', reference);
    set('body', emailBody(reference, d, lines, at));
    save(KEY_PENDING, { reference: reference, at: at, percent: DISCOUNT_PERCENT, gstPercent: GST_PERCENT, details: d, lines: lines, submitted: false });
    var button = $('[data-submit-button]');
    button.textContent = 'Sending…';
    // Block extra clicks without disabling the button: Shopify's spam protection (hCaptcha) holds the
    // first submit and re-submits through this button, which a disabled button would prevent.
    button.classList.add('is-busy');
    button.setAttribute('aria-busy', 'true');
    // The form then posts normally to Shopify, which emails it to the store and returns here.
  });

  function showDone(pending) {
    var percent = pending.percent == null ? DISCOUNT_PERCENT : pending.percent;
    var t = summarize(pending.lines, percent, pending.gstPercent);
    $('[data-done-reference]').textContent = pending.reference;
    $('[data-done-date]').textContent = formatDate(pending.at);
    $('[data-done-email]').textContent = pending.details.email;
    $('[data-done-status]').textContent = config.status;
    $('[data-done-summary]').innerHTML = linesTableHTML(pending.lines) + totalsHTML(t) + detailsHTML(pending.details);
    buildPrintDocument(pending, t);
  }

  /* A formal A4 indent, separate from the page layout. Hidden on screen; the print stylesheet shows only
     this document when the confirmation is printed or saved as PDF. */
  function buildPrintDocument(pending, t) {
    var doc = document.querySelector('.indent-doc');
    if (!doc) {
      doc = document.createElement('div');
      doc.className = 'indent-doc';
      doc.setAttribute('aria-hidden', 'true');
      document.body.appendChild(doc);
    }
    var b = config.brand || {};
    var d = pending.details;
    var contact = [b.website, b.email, b.phone, b.address].filter(Boolean).map(esc).join(' · ');
    var row = function (label, value, cls) { return '<tr' + (cls ? ' class="' + cls + '"' : '') + '><td>' + label + '</td><td>' + value + '</td></tr>'; };
    doc.innerHTML =
      '<header class="doc-head">' +
        '<div class="doc-brand">' + (b.logo ? '<img src="' + esc(b.logo) + '" alt="">' : '<strong>' + esc(b.name) + '</strong>') +
          (contact ? '<p>' + contact + '</p>' : '') + '</div>' +
        '<div class="doc-title"><h1>Bookstore Bulk Indent</h1><dl>' +
          '<dt>Reference</dt><dd>' + esc(pending.reference) + '</dd>' +
          '<dt>Submitted</dt><dd>' + esc(formatDate(pending.at)) + '</dd>' +
        '</dl><span class="doc-status">' + esc(config.status) + '</span>' +
        '<p class="doc-sub">Awaiting Confirmation · Indent request — not a tax invoice</p></div>' +
      '</header>' +
      '<section class="doc-parties">' +
        '<div><h2>Bookstore</h2><p><strong>' + esc(d.business) + '</strong><br>' + esc(d.contact) + '<br>' + esc(d.mobile) + '<br>' + esc(d.email) +
          '<br>GSTIN: ' + esc(d.gstin || 'Not provided') + '</p></div>' +
        '<div><h2>Billing address</h2><p>' + esc(addressText(d, 'bill')) + '</p></div>' +
        '<div><h2>Delivery address</h2><p>' + esc(addressText(d, 'ship')) + '</p></div>' +
      '</section>' +
      '<table class="doc-table"><thead><tr><th>#</th><th>Book</th><th>Class</th><th>Subject</th><th class="num">Qty</th><th class="num">Unit price</th><th class="num">Line subtotal</th></tr></thead><tbody>' +
        pending.lines.map(function (l, i) {
          return '<tr><td>' + (i + 1) + '</td><td>' + esc(l.title) + '</td><td>' + esc(l.cls || '—') + '</td>' +
            '<td>' + esc([l.subject, l.stream].filter(Boolean).join(' · ') || '—') + '</td>' +
            '<td class="num">' + num(l.qty) + '</td><td class="num">' + money(l.price) + '</td><td class="num">' + money(l.price * l.qty) + '</td></tr>';
        }).join('') +
      '</tbody></table>' +
      '<div class="doc-bottom">' +
        '<div>' +
          (d.notes ? '<h2 class="doc-section-title">Order notes</h2><p>' + esc(d.notes) + '</p>' : '') +
          '<p class="doc-note">' + esc(config.disclaimer) + (config.notConfirmed ? ' ' + esc(config.notConfirmed) : '') + '</p>' +
        '</div>' +
        '<table class="doc-summary"><tbody>' +
          row('Titles', num(t.titles)) +
          row('Total books', num(t.books)) +
          row('Subtotal before discount', money(t.subtotal)) +
          (t.percent ? row('Bookstore bulk discount (' + t.percent + '%)', '−' + money(t.discount)) : '') +
          row('Subtotal after discount', money(t.afterDiscount)) +
          row(gstLabel(t), moneyExact(t.gst)) +
          row('Delivery charges', '<span class="muted-cell">If applicable</span>') +
          row(estimateLabel() + ' (estimate)', money(t.total), 'total') +
          '<tr><td colspan="2" class="muted-cell">+ delivery charges, if applicable</td></tr>' +
        '</tbody></table>' +
      '</div>' +
      '<p class="doc-foot">' + esc(estimateNote()) + ' Prices shown are catalogue prices. Reference ' + esc(pending.reference) + '.</p>';
  }

  /* ---------- steps / routing ---------- */
  var STEPS = ['catalogue', 'indent', 'details', 'review', 'done'];
  function go(step) { if (location.hash !== '#' + step) location.hash = step; else route(); }
  function route() {
    var step = (location.hash || '#catalogue').slice(1);
    if (STEPS.indexOf(step) === -1) step = 'catalogue';
    var pending = load(KEY_PENDING);
    if (step === 'done' && !(pending && pending.submitted)) step = 'catalogue';
    if (step !== 'catalogue' && step !== 'done' && !indentLines().length) step = 'catalogue';
    if (step === 'review' && !validateDetails(false).ok) step = 'details';

    $$('[data-step]').forEach(function (el) { el.hidden = el.getAttribute('data-step') !== step; });
    $$('[data-step-indicator]').forEach(function (el) {
      var name = el.getAttribute('data-step-indicator');
      el.classList.toggle('is-done', step === 'done' || STEPS.indexOf(name) < STEPS.indexOf(step));
      if (name === step) el.setAttribute('aria-current', 'step'); else el.removeAttribute('aria-current');
    });
    $('[data-indent-bar]').hidden = step !== 'catalogue';
    // The formal printable indent only exists for a submitted indent.
    document.body.classList.toggle('has-indent-doc', step === 'done');

    if (step === 'indent') renderIndent();
    if (step === 'review') renderReview();
    if (step === 'done') showDone(pending);
    if (step === 'catalogue') $$('.bulk-row').forEach(syncRow);

    var top = root.getBoundingClientRect().top + window.pageYOffset - 80;
    if (window.pageYOffset > top) window.scrollTo(0, top);
  }

  function showBanner(html) {
    var banner = $('[data-bulk-banner]');
    banner.innerHTML = html || '';
    banner.hidden = !html;
  }

  /* ---------- events ---------- */
  root.addEventListener('click', function (e) {
    var t = e.target;
    var link = t.closest('[data-goto]');
    if (link) { e.preventDefault(); go(link.getAttribute('data-goto')); return; }
    if (t.closest('[data-commit]')) { commitRow(t.closest('.bulk-row')); return; }
    if (t.closest('[data-remove]')) { setQty(t.closest('tr').getAttribute('data-id'), 0); renderIndent(); return; }
    if (t.closest('[data-proceed-details]')) { go('details'); return; }
    if (t.closest('[data-print]')) { window.print(); return; }
    if (t.closest('[data-new-indent]')) { drop(KEY_PENDING); showBanner(''); go('catalogue'); }
  });

  root.addEventListener('input', function (e) {
    var t = e.target;
    if (t.matches('[data-filter-search]')) { clearTimeout(filterTimer); filterTimer = setTimeout(applyFilters, 120); return; }
    if (t.matches('.bulk-row .bulk-qty')) { syncRow(t.closest('.bulk-row')); return; }
    if (t.matches('[data-delivery-field]') && form.elements.sameAsDelivery.checked) syncBilling(false);
    if (t.form === form) save(KEY_DETAILS, readDetails(), 'sessionStorage');
  });

  root.addEventListener('change', function (e) {
    var t = e.target;
    if (t.matches('[data-filter-class], [data-filter-stream], [data-filter-selected]')) { applyFilters(); return; }
    if (t.matches('.bulk-row .bulk-qty')) { commitRow(t.closest('.bulk-row')); return; }
    if (t.matches('[data-indent-qty]')) {
      var row = t.closest('tr');
      var parsed = parseQty(t.value, t);
      var error = row.querySelector('.bulk-row__error');
      if (!parsed.ok) { t.setAttribute('aria-invalid', 'true'); error.textContent = parsed.message; return; }
      setQty(row.getAttribute('data-id'), parsed.qty);
      renderIndent();
      return;
    }
    if (t.matches('[data-same-as-delivery]')) syncBilling(true);
    if (t.matches('[data-delivery-field]') && form.elements.sameAsDelivery.checked) syncBilling(false);
    if (t.form === form) save(KEY_DETAILS, readDetails(), 'sessionStorage');
  });

  root.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches('.bulk-row .bulk-qty')) { e.preventDefault(); commitRow(e.target.closest('.bulk-row')); }
  });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var result = validateDetails(true);
    showFormErrors(result.errors);
    if (!result.ok) return;
    save(KEY_DETAILS, readDetails(), 'sessionStorage');
    go('review');
  });

  window.addEventListener('hashchange', route);

  /* ---------- start ---------- */
  fillDetails(load(KEY_DETAILS, 'sessionStorage'));
  syncBilling(false);

  // Returning from Shopify after the indent form was posted.
  var pending = load(KEY_PENDING);
  var postedOk = !!document.querySelector('[data-posted-ok]') || /[?&]contact_posted=true/.test(location.search);
  var postFailed = !!document.querySelector('[data-submit-errors]');
  if (pending && !pending.submitted && postedOk) {
    pending.submitted = true;
    save(KEY_PENDING, pending);
    indent = {};
    save(KEY_INDENT, indent);
    if (history.replaceState) history.replaceState(null, '', location.pathname + '#done');
    else location.hash = 'done';
  } else if (postFailed) {
    location.hash = 'review';
  }

  loadCatalogue().then(function () {
    var missing = Object.keys(indent).filter(function (id) { return !byId[id]; });
    if (missing.length) {
      missing.forEach(function (id) { delete indent[id]; });
      save(KEY_INDENT, indent);
      showBanner('<p>' + plural(missing.length, 'book') + ' in your saved indent ' + (missing.length === 1 ? 'is' : 'are') + ' no longer in our catalogue and ' + (missing.length === 1 ? 'was' : 'were') + ' removed.</p>');
    }
    renderCatalogue();
    applyFilters();
    updateIndentBar();
    route();
  }).catch(function () {
    $('[data-catalogue-status]').textContent = 'The catalogue could not be loaded. Please refresh the page, or contact us with your book list.';
    route();
  });
})();
